/**
 * GlitchReplay funnel registry — "SaaS funnel" KB pages.
 *
 * KB articles listed here replace the consulting ContextualServiceCTA with a
 * GlitchReplay product CTA (glitchreplay.com) in the KB sidebar. A page is
 * either a funnel page or an ads page (consumer-pages.ts), never both — none
 * of these slugs appear in CONSUMER_KB_ARTICLES.
 *
 * Source cluster: the AI-coding-assistant KB articles (Claude Code, OpenAI
 * Codex CLI, GitHub Copilot CLI, Gemini CLI). The audience is developers
 * actively shipping AI-generated code — exactly who needs error tracking +
 * session replay for when that code breaks in production. (~13k pageviews/mo
 * across the cluster.)
 *
 * Keyed by `${category}/${slug}`.
 */
export const GLITCHREPLAY_KB_FUNNEL = new Set<string>([
  // NOTE: model-switching / local-model / rate-limit articles live in
  // WIDEAREAAI_KB_FUNNEL (wideareaai-funnel.ts) instead — local-GPU intent
  // beats error-tracking intent there. Keep the two sets disjoint.

  // Claude (Claude Code CLI)
  'claude/how-to-configure-claude-md',
  'claude/how-to-create-custom-commands',
  'claude/how-to-fix-freezing-issues',
  'claude/how-to-fix-installation-errors',
  'claude/how-to-fix-permission-errors',
  'claude/how-to-install-claude-code-cli',
  'claude/how-to-manage-permissions-and-sandboxing',
  'claude/how-to-set-up-mcp-servers',
  'claude/how-to-use-background-agents',
  'claude/how-to-use-git-worktrees',
  'claude/how-to-use-hooks-for-automation',
  'claude/how-to-use-plan-mode',
  'claude/how-to-use-vscode-integration',
  'claude/how-to-work-with-large-codebases',
  'claude/where-configuration-files-are-stored',

  // Gemini (Gemini CLI)
  'gemini/how-to-configure-mcp-integrations',
  'gemini/how-to-fix-permission-denied-errors',
  'gemini/how-to-fix-sandboxing-errors',
  'gemini/how-to-fix-ssl-errors',
  'gemini/how-to-fix-wsl-installation',
  'gemini/how-to-install-google-gemini-cli',
  'gemini/how-to-leverage-1m-token-context',
  'gemini/how-to-manage-extensions',
  'gemini/how-to-set-up-api-key',
  'gemini/how-to-set-up-vertex-ai-enterprise',
  'gemini/how-to-use-batch-code-analysis',
  'gemini/how-to-use-checkpointing',
  'gemini/how-to-use-git-worktrees',
  'gemini/how-to-use-google-search-grounding',
  'gemini/how-to-use-headless-mode',
  'gemini/how-to-use-yolo-mode',
  'gemini/where-configuration-files-are-stored',

  // OpenAI (Codex CLI)
  'openai/how-to-configure-sandbox-modes',
  'openai/how-to-fix-context-errors',
  'openai/how-to-fix-slow-performance',
  'openai/how-to-integrate-with-ide',
  'openai/how-to-resume-sessions',
  'openai/how-to-set-up-mcp-servers',
  'openai/how-to-use-git-worktrees',
  'openai/how-to-use-image-input',
  'openai/how-to-use-slash-commands',
  'openai/where-configuration-files-are-stored',

  // Copilot (GitHub Copilot CLI)
  'copilot/how-to-fix-authorization-errors',
  'copilot/how-to-fix-homebrew-installation-errors',
  'copilot/how-to-fix-proxy-issues',
  'copilot/how-to-generate-commits-and-prs',
  'copilot/how-to-install-github-copilot-cli',
  'copilot/how-to-integrate-cicd',
  'copilot/how-to-migrate-from-gh-copilot',
  'copilot/how-to-set-up-enterprise',
  'copilot/how-to-set-up-mcp-servers',
  'copilot/how-to-set-up-sso-saml',
  'copilot/how-to-use-agents',
  'copilot/how-to-use-delegate-command',
  'copilot/how-to-use-git-worktrees',
  'copilot/how-to-use-shell-commands',
  'copilot/how-to-use-slash-commands',
  'copilot/how-to-use-specialized-agents',
  'copilot/how-to-use-with-github-actions',
  'copilot/how-to-work-with-monorepos',
  'copilot/where-configuration-files-are-stored',
]);

/** True when a KB article should show the GlitchReplay funnel CTA. */
export function isGlitchReplayFunnelKB(category: string, slug: string): boolean {
  return GLITCHREPLAY_KB_FUNNEL.has(`${category}/${slug}`);
}
